import React, { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import {
  Aperture,
  AlertTriangle,
  Bookmark,
  Camera,
  Calendar,
  CircleAlert,
  CircleCheck,
  FileText,
  MapPin,
  Copyright,
  SlidersHorizontal,
  MoreHorizontal,
  PanelRightOpen,
  LockKeyhole,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  AlertAction,
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
  Input,
} from "@exiftrator/shared/components/ui";
import {
  EXIF_FIELDS,
  FIELD_CATEGORIES,
  CATEGORY_METADATA,
} from "../../config/fieldDefinitions";
import { useBasePath } from "../../contexts/BasePathContext";
import { useAppContext } from "../../contexts/AppContext";
import { useDemo } from "../../contexts/DemoContext";
import { useFeatureAccess } from "../../contexts/LicenseContext";
import { useIsMobile, useIsTablet } from "../../hooks/useMobile";
import FieldCardRow from "./FieldCardRow";
import {
  HeaderActionButton,
  HeaderActionGroup,
  HeaderIconButton,
} from "../Header/HeaderActions";
import {
  OVERFLOW_MENU_CONTENT_CLASS,
  OVERFLOW_MENU_ITEM_CLASS,
  OVERFLOW_MENU_LABEL_CLASS,
  OVERFLOW_MENU_SEPARATOR_CLASS,
  OVERFLOW_MENU_SHORTCUT_CLASS,
} from "../Header/overflowMenuStyles";
import { PanelHeader } from "../PanelHeader";
import { cn } from "../utils/cn";
import {
  WORKSPACE_SPACING,
  getDensityClass,
} from "../Workspace/workspaceSpacing";
import {
  METADATA_ACTION_DIALOG_COPY,
  getMetadataActionDialogText,
} from "../common/metadataActionDialogCopy";

const LOCATION_FIELD_ORDER = ["latitude", "longitude", "altitude"];
const SIDE_BY_SIDE_FIELD_GROUPS = {
  [FIELD_CATEGORIES.CAMERA]: ["cameraMake", "cameraModel"],
  [FIELD_CATEGORIES.LENS]: ["lensMake", "lensModel"],
};

const CATEGORY_ICON_MAP = {
  [FIELD_CATEGORIES.DATE_TIME]: Calendar,
  [FIELD_CATEGORIES.LOCATION]: MapPin,
  [FIELD_CATEGORIES.CAMERA]: Camera,
  [FIELD_CATEGORIES.LENS]: Aperture,
  [FIELD_CATEGORIES.IMAGE_SETTINGS]: SlidersHorizontal,
  [FIELD_CATEGORIES.DESCRIPTIVE]: FileText,
  [FIELD_CATEGORIES.COPYRIGHT]: Copyright,
};

const ISSUE_SEVERITY_META = {
  high: {
    icon: AlertTriangle,
    variant: "warning",
    label: "High",
  },
  medium: {
    icon: CircleAlert,
    variant: "default",
    label: "Medium",
  },
  low: {
    icon: CircleCheck,
    variant: "default",
    label: "Low",
  },
};

function MetadataStudioPane({
  model,
  issuesApi,
  desktopRightOpen = true,
  onToggleDesktopRight = () => {},
}) {
  const centerPaneContentWidthClass = "w-full lg:mx-auto lg:max-w-[800px]";
  const navigate = useNavigate();
  const { appPath } = useBasePath();
  const { isDemo } = useDemo();
  const {
    setKeyboardShortcutsModalOpen,
    setKeyboardShortcutsInitialView,
  } = useAppContext();
  const presetsAccess = useFeatureAccess("presets");
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const paneInsetXClass = getDensityClass(
    isMobile,
    isTablet,
    WORKSPACE_SPACING.paneInsetX,
  );
  const paneInsetYClass = getDensityClass(
    isMobile,
    isTablet,
    WORKSPACE_SPACING.paneInsetY,
  );
  const {
    files,
    primaryIndex,
    selectedIndices,
    enabledFieldsByCategory,
    visibleCategoryIds,
    formData,
    fieldErrors,
    hasMultiple,
    isFieldEdited,
    openCategoryIds,
    setOpenCategoryIds,
    handleChange,
    handleCommit,
    handleRevertField,
    categoryHasValues,
    handleClearCategory,
    handleRestoreOriginalMetadata,
    handleClearAllMetadata,
    handleRemoveSelectedFiles,
    handleOpenLocationModal,
    handleOpenPresetModal,
  } = model;

  const [searchQuery, setSearchQuery] = useState("");
  const [confirmAction, setConfirmAction] = useState(null);
  const fieldRefs = useRef(new Map());
  const issues = issuesApi?.issues || [];
  const issueCounts = issuesApi?.issueCounts || { high: 0, medium: 0, low: 0 };
  const runFix = issuesApi?.runFix || (() => {});

  const normalizedQuery = searchQuery.trim().toLowerCase();

  const fieldMatchesQuery = useCallback(
    (field) => {
      if (!normalizedQuery) return true;
      return (
        field.label.toLowerCase().includes(normalizedQuery) ||
        field.id.toLowerCase().includes(normalizedQuery)
      );
    },
    [normalizedQuery],
  );

  const filteredCategories = useMemo(() => {
    const result = {};

    visibleCategoryIds.forEach((categoryId) => {
      const fields = enabledFieldsByCategory[categoryId] || [];
      const filteredFields = fields.filter(fieldMatchesQuery);

      if (filteredFields.length > 0) {
        result[categoryId] = filteredFields;
      }
    });

    return result;
  }, [visibleCategoryIds, enabledFieldsByCategory, fieldMatchesQuery]);

  const renderField = useCallback(
    (field) => (
      <FieldCardRow
        key={field.id}
        ref={(node) => {
          if (node) {
            fieldRefs.current.set(field.id, node);
          } else {
            fieldRefs.current.delete(field.id);
          }
        }}
        field={field}
        value={formData[field.id]}
        hasMultiple={Boolean(hasMultiple[field.id])}
        isEdited={Boolean(isFieldEdited[field.id])}
        error={fieldErrors[field.id]}
        onChange={handleChange}
        onCommit={handleCommit}
        onRevert={handleRevertField}
      />
    ),
    [
      formData,
      hasMultiple,
      isFieldEdited,
      fieldErrors,
      handleChange,
      handleCommit,
      handleRevertField,
    ],
  );

  const renderCategoryFields = useCallback(
    (categoryId, fields) => {
      if (categoryId === FIELD_CATEGORIES.LOCATION) {
        const ordered = [...fields].sort((a, b) => {
          const aIndex = LOCATION_FIELD_ORDER.indexOf(a.id);
          const bIndex = LOCATION_FIELD_ORDER.indexOf(b.id);
          return (aIndex === -1 ? 99 : aIndex) - (bIndex === -1 ? 99 : bIndex);
        });

        const coordinateFields = ordered.filter(
          (field) => field.id === "latitude" || field.id === "longitude",
        );
        const otherFields = ordered.filter(
          (field) => field.id !== "latitude" && field.id !== "longitude",
        );

        return (
          <div className="space-y-3">
            <Button
              variant="outline"
              size="sm"
              className="w-full justify-start"
              onClick={handleOpenLocationModal}
            >
              <MapPin className="size-4" />
              Search location
            </Button>

            {coordinateFields.length > 0 && (
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                {coordinateFields.map((field) => renderField(field))}
              </div>
            )}

            {otherFields.map((field) => renderField(field))}
          </div>
        );
      }

      const sideBySideIds = SIDE_BY_SIDE_FIELD_GROUPS[categoryId];
      if (!sideBySideIds) {
        return (
          <div className="space-y-3">
            {fields.map((field) => renderField(field))}
          </div>
        );
      }

      const fieldsById = new Map(fields.map((field) => [field.id, field]));
      const groupedFields = sideBySideIds
        .map((fieldId) => fieldsById.get(fieldId))
        .filter(Boolean);
      const groupedSet = new Set(groupedFields.map((field) => field.id));
      const remainingFields = fields.filter(
        (field) => !groupedSet.has(field.id),
      );

      return (
        <div className="space-y-3">
          {groupedFields.length > 1 && (
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              {groupedFields.map((field) => renderField(field))}
            </div>
          )}
          {groupedFields.length <= 1 &&
            groupedFields.map((field) => renderField(field))}
          {remainingFields.map((field) => renderField(field))}
        </div>
      );
    },
    [handleOpenLocationModal, renderField],
  );

  const primaryFile = files[primaryIndex];

  const title =
    selectedIndices.size === 0 ? (
      "Select files to edit"
    ) : selectedIndices.size === 1 ? (
      <div className="flex min-w-0 items-center gap-2">
        <span className="truncate">
          {primaryFile?.name || "Metadata Studio"}
        </span>
      </div>
    ) : (
      `Batch editing ${selectedIndices.size} files`
    );

  const metadataConfirmText = confirmAction
    ? getMetadataActionDialogText(confirmAction, selectedIndices.size)
    : null;

  const confirmMeta =
    confirmAction === "remove" && !isDemo
      ? {
          title: "Remove selected files",
          description:
            "This will remove the selected files from the current session.",
          confirmLabel: "Remove",
          confirmClassName: "bg-destructive text-white hover:bg-destructive/90",
          onConfirm: handleRemoveSelectedFiles,
        }
      : confirmAction === "restore"
        ? {
            ...metadataConfirmText,
            confirmClassName: "",
            onConfirm: handleRestoreOriginalMetadata,
          }
        : confirmAction === "clear"
          ? {
              ...metadataConfirmText,
              confirmClassName:
                "bg-destructive text-white hover:bg-destructive/90",
              onConfirm: handleClearAllMetadata,
            }
        : null;

  return (
    <section className="flex min-h-0 flex-1 flex-col bg-bg-workspace">
      <PanelHeader
        tone="contrast"
        title={title}
        rightContent={
          <HeaderActionGroup>
            <HeaderActionButton
              intent="secondary"
              onClick={handleOpenPresetModal}
              title={
                !presetsAccess.allowed ? "Presets require Pro" : "Open presets"
              }
            >
              <Bookmark className="size-4" />
              Presets
            </HeaderActionButton>

            {!isMobile && !desktopRightOpen && (
              <HeaderIconButton
                intent="utility"
                onClick={onToggleDesktopRight}
                aria-label="Show assistant panel"
                title="Show assistant panel"
              >
                <PanelRightOpen className="size-4" />
              </HeaderIconButton>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <HeaderIconButton
                  intent="utility"
                  aria-label="Metadata options"
                  title="Metadata options"
                >
                  <MoreHorizontal className="size-4" />
                </HeaderIconButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className={OVERFLOW_MENU_CONTENT_CLASS}
              >
                <DropdownMenuGroup>
                  <DropdownMenuLabel
                    className={cn(
                      OVERFLOW_MENU_LABEL_CLASS,
                      "text-[10px] uppercase tracking-[0.08em]",
                    )}
                  >
                    Metadata Actions
                  </DropdownMenuLabel>
                  <DropdownMenuItem
                    disabled={selectedIndices.size === 0}
                    className={OVERFLOW_MENU_ITEM_CLASS}
                    onClick={() => setConfirmAction("restore")}
                  >
                    {METADATA_ACTION_DIALOG_COPY.restore.actionLabel}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    disabled={selectedIndices.size === 0}
                    className={OVERFLOW_MENU_ITEM_CLASS}
                    onClick={() => setConfirmAction("clear")}
                  >
                    {METADATA_ACTION_DIALOG_COPY.clear.actionLabel}
                  </DropdownMenuItem>
                </DropdownMenuGroup>
                <DropdownMenuSeparator className={OVERFLOW_MENU_SEPARATOR_CLASS} />
                <DropdownMenuItem
                  variant="destructive"
                  disabled={selectedIndices.size === 0 || isDemo}
                  className={OVERFLOW_MENU_ITEM_CLASS}
                  onClick={() => setConfirmAction("remove")}
                >
                  Remove selected files
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    setKeyboardShortcutsInitialView("editor");
                    setKeyboardShortcutsModalOpen(true);
                  }}
                  className={OVERFLOW_MENU_ITEM_CLASS}
                >
                  <span>Keyboard shortcuts</span>
                  <DropdownMenuShortcut className={OVERFLOW_MENU_SHORTCUT_CLASS}>
                    ?
                  </DropdownMenuShortcut>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </HeaderActionGroup>
        }
      />

      <div
        className={cn(
          "min-h-0 flex-1 overflow-y-auto",
          WORKSPACE_SPACING.sectionGapPrimary,
          paneInsetXClass,
          paneInsetYClass,
        )}
      >
        <div className={centerPaneContentWidthClass}>
        {!presetsAccess.allowed && (
          <Alert className="border-accent-primary/30 bg-accent-primary/10">
            <LockKeyhole className="size-4 text-accent-primary" />
            <AlertTitle>Presets are locked on Free</AlertTitle>
            <AlertDescription>
              Upgrade to Pro to apply presets from the editor workflow.
            </AlertDescription>
          </Alert>
        )}

        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-[11px] font-semibold uppercase tracking-wider text-text-secondary">
              Issues
            </h2>
            <div className="text-xs text-text-secondary">
              {issueCounts.high} high • {issueCounts.medium} medium •{" "}
              {issueCounts.low} low
            </div>
          </div>
          <div className="space-y-3">
            {issues.length === 0 ? (
              <Alert variant="default">
                <div className="flex items-start gap-2">
                  <CircleCheck className="mt-0.5 size-4 text-text-secondary" />
                  <div>
                    <AlertTitle>No issues found</AlertTitle>
                    <AlertDescription>
                      No metadata issues detected in the current selection.
                    </AlertDescription>
                  </div>
                </div>
              </Alert>
            ) : (
              issues.map((issue) => {
                const meta =
                  ISSUE_SEVERITY_META[issue.severity] ||
                  ISSUE_SEVERITY_META.medium;
                const Icon = meta.icon;

                return (
                  <Alert
                    key={issue.id}
                    variant={meta.variant}
                    className={
                      meta.variant === "warning"
                        ? ""
                        : "border-border-default/70 bg-bg-secondary/75"
                    }
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <Icon className="size-4 text-text-secondary" />
                          <AlertTitle>{issue.message}</AlertTitle>
                        </div>
                        <AlertDescription className="text-xs">
                          {meta.label} severity • Affects{" "}
                          {issue.affectedIndices.length} file
                          {issue.affectedIndices.length > 1 ? "s" : ""}
                        </AlertDescription>
                      </div>
                      <div className="flex shrink-0 flex-wrap items-center gap-2">
                        {issue.fixes.map((fix) => (
                          <AlertAction
                            key={fix.id}
                            onClick={() => runFix(issue.id, fix.id)}
                          >
                            {fix.label}
                          </AlertAction>
                        ))}
                      </div>
                    </div>
                  </Alert>
                );
              })
            )}
          </div>
        </section>

        <section className="space-y-3">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-[11px] font-semibold uppercase tracking-wider text-text-secondary">
              Advanced Metadata
            </h2>
            <Input
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search fields"
              className="h-8 w-[220px]"
            />
          </div>
          <div className={WORKSPACE_SPACING.sectionGapTight}>
            <Accordion
              type="multiple"
              value={openCategoryIds}
              onValueChange={setOpenCategoryIds}
              className={WORKSPACE_SPACING.sectionGapTight}
            >
              {Object.entries(filteredCategories).map(
                ([categoryId, fields]) => {
                  const CategoryIcon = CATEGORY_ICON_MAP[categoryId];

                  return (
                    <AccordionItem
                      key={categoryId}
                      value={categoryId}
                      className={cn(
                        "overflow-hidden rounded-xl border border-border-subtle bg-bg-secondary/70",
                        WORKSPACE_SPACING.innerSurfaceInsetX,
                      )}
                    >
                      <AccordionTrigger className="py-3 hover:no-underline">
                        <div className="flex items-center gap-2">
                          {CategoryIcon && (
                            <CategoryIcon className="size-4 text-text-secondary" />
                          )}
                          <span className="text-sm font-semibold text-text-primary">
                            {CATEGORY_METADATA[categoryId]?.label || categoryId}
                          </span>
                        </div>
                      </AccordionTrigger>

                      <AccordionContent className="pb-4">
                        {renderCategoryFields(categoryId, fields)}

                        <div className="mt-3">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleClearCategory(categoryId)}
                            disabled={!categoryHasValues(categoryId)}
                          >
                            Reset category
                          </Button>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  );
                },
              )}
            </Accordion>
          </div>
        </section>
        </div>
      </div>

      <Dialog
        open={Boolean(confirmMeta)}
        onOpenChange={(open) => {
          if (!open) setConfirmAction(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{confirmMeta?.title}</DialogTitle>
            <DialogDescription>{confirmMeta?.description}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmAction(null)}>
              Cancel
            </Button>
            <Button
              className={confirmMeta?.confirmClassName}
              onClick={() => {
                confirmMeta?.onConfirm?.();
                setConfirmAction(null);
              }}
            >
              {confirmMeta?.confirmLabel || "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

export default MetadataStudioPane;
